import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProductsTabSearchOptions } from '../../redux/actions/productActions';
import { debounce } from 'throttle-debounce';
import { apiCallDebounceTime } from '../../Constants/Api';
import { productJsonProps, productElasticSearchMatchFields } from '../../Constants/Products';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { matchSorter } from 'match-sorter';
import { CircularProgress, TextField } from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import { get } from 'lodash';
import ProductWithThumbnail from './ProductWithThumbnail';
import { getProductImgUrl } from '../../Utils/ProductUtils';
import ProductRefreshBtn from '../ManageProduct/ProductRefreshBtn';

const productSearchPlaceHolderTxt = 'Search by product id, name, or ivm etc';

// Made this as a re-usable search component for products.
const ProductSearch = (props) => {
	const dispatch = useDispatch();
	const options = useSelector((state) => state.productssearchoptions);
	// Controlling states of autocomplete for input value and value.
	// value This state represents the value selected by the user, for instance when pressing Enter.
	const [value, setValue] = useState('');
	// inputValue This state represents the value displayed in the textbox.
	const [inputValue, setInputValue] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const componentLoaded = useRef(true);
	//implementing debounce for products search.
	//only trigger API call when there is a delay of mentioned time from last keystroke
	const DebounceProductSearch = useCallback(
		debounce(apiCallDebounceTime, false, (value) =>
			dispatch(getProductsTabSearchOptions(value)).then(() => {
				if (componentLoaded.current) {
					setIsLoading(false);
				}
			}),
		),
		[],
	);
	// trigger API call for populate products dropdown
	const OnInputChange = (event, value) => {
		if (value) {
			setIsLoading(true);
			DebounceProductSearch(value);
		}
		// calling inputChange function provided from parent components.
		props.onInputChange(value);
		setInputValue(value);
	};
	// using two useEffects since one is dependent on props to set controlled states.
	useEffect(() => {
		setInputValue(props.inputValue);
		setValue(props.inputValue);
	}, [props]); // Using an empty dependency array ensures this only runs on unmount
	// below useEffect is not dependent on any state changes, only should set below state on initial load.
	useEffect(() => {
		return () => {
			componentLoaded.current = false;
		};
	}, []); // Using an empty dependency array ensures this only runs on unmount
	//assigning products to asset on selection of dropdown.
	//avoiding already assigned product
	const onValueChange = async (event, valueObj) => {
		// skip selection if clicked on refresh button, displayed as option.
		if (valueObj && valueObj.inputValue) {
			return;
		}
		if (props.onValueChange && valueObj && typeof valueObj === 'object') {
			const productName = get(valueObj, productJsonProps.name);
			setValue(productName);
			props.onValueChange(valueObj);
		} else {
			setValue('');
		}
	};
	const getOptionsLabel = (option) => {
		if (option.inputValue) {
			return option.inputValue;
		}
		const productName = get(option, productJsonProps.name);
		if (productName) {
			return productName;
		}
		// avoid returning objects, only return a string as label.
		if (typeof option === 'string') {
			return option;
		}
	};
	const filterOptions = (options, { inputValue }) => {
		// check for only product option with refresh button.
		if (inputValue && options.length === 1 && options[0].inputValue === inputValue) {
			return options;
		}
		//only trigger filtering when we have search term and options
		if (options.length > 0 && inputValue && inputValue.length > 0) {
			const result = matchSorter(options, inputValue, {
				//providing list of fields as filtering keys to enable multi-field filters.
				keys: productElasticSearchMatchFields,
			});
			return result;
		} else {
			return [];
		}
	};
	return (
		<Autocomplete
			id={`product-search-${props.id}`}
			data-cy="product-search"
			value={value}
			inputValue={inputValue}
			label="product search"
			onInputChange={OnInputChange}
			onChange={onValueChange}
			filterOptions={filterOptions}
			getOptionLabel={getOptionsLabel}
			clearOnBlur={false}
			options={options}
			loading={isLoading}
			loadingText={'Loading...'}
			key={`product-search-${props.id}`}
			renderOption={(option) => {
				// if option has inputValue and shouldRefresh add a refresh button for missing product.
				if (option.inputValue && option.shouldRefresh) {
					return <ProductRefreshBtn productId={option.inputValue} />;
				}
				const productName = get(option, productJsonProps.name);
				const productId = get(option, productJsonProps.productId);
				return (
					<ProductWithThumbnail
						productName={`${productName} - ${productId}`}
						url={getProductImgUrl(option)}
					/>
				);
			}}
			openOnFocus={true}
			renderInput={(
				params, //rendering text field with loading icon and results when making calls to API service
			) => (
				<TextField
					{...params}
					placeholder={productSearchPlaceHolderTxt}
					variant="outlined"
					InputProps={{
						...params.InputProps,
						startAdornment: props.displayStartAdornment ? props.startAdornment ?? <SearchIcon /> : '',
						endAdornment: (
							<React.Fragment>
								{isLoading ? <CircularProgress color="inherit" size={20} /> : null}
							</React.Fragment>
						),
					}}
				/>
			)}
		/>
	);
};
export default ProductSearch;
